import React, { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Confetti } from "@phosphor-icons/react";
import { LoginAuthContext } from "Context/Authentication/LoginAuth";
import usePostDataNoLang from "Hooks/usePostDataNoLang";
import useApiQuery from "Hooks/useApiQuery";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "Components/RequestHandler/Spinner";

const Success = () => {
  const { t } = useTranslation();
  const { loginHandler, setIsAuthenticated, setAllowedCoursesHandler } =
    useContext(LoginAuthContext);
  const navigate = useNavigate();
  const { postData } = usePostDataNoLang();
  const location = useLocation();
  const [source, setSource] = useState(null);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [purchaseType, setPurchaseType] = useState(null);

  // Fetch categories for interest selection
  const { data: categoriesData } = useApiQuery("course-categories");
  const categories = categoriesData?.data || [];

  const redirectToApp = (sessionId) => {
    const iOSDeepLink = `deeclass://payment/success?session_id=${sessionId}`;
    const androidDeepLink = `intent://payment/success?session_id=${sessionId}#Intent;scheme=deeclass;package=com.deeclass.app;end`;

    const isAndroid = /android/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isAndroid) {
      window.location.href = androidDeepLink;
    } else if (isIOS) {
      window.location.href = iOSDeepLink;
    } else {
      window.location.href = iOSDeepLink;
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const sourceValue = queryParams.get("source");
    const sessionId = queryParams.get("session_id");
    const type = queryParams.get("type");
    setSource(sourceValue);
    setPurchaseType(type);

    if (sourceValue === "application") {
      redirectToApp(sessionId);
      return;
    }

    refreshData(sessionId, type);
  }, [location]);

  const refreshData = async (sessionId, type) => {
    const storedToken = localStorage.getItem("token");
    let storedUser = null;

    try {
      const storedUserString = localStorage.getItem("user");
      if (storedUserString && storedUserString !== "undefined") {
        storedUser = JSON.parse(storedUserString);
      }
    } catch (error) {
      navigate("/");
      return;
    }

    if (!storedUser?._id || !storedToken) {
      navigate("/");
      return;
    }

    const response = await postData("webhook/refresh-data", {
      user_id: storedUser._id,
      session_id: sessionId,
    });

    if (response.success) {
      const mobileUser = response?.data?.mobile_user;

      loginHandler(
        storedToken,
        mobileUser,
        mobileUser?.allowed_profiles,
        mobileUser?.allowed_courses,
        mobileUser?.profiles
      );
      setAllowedCoursesHandler(mobileUser?.allowed_courses);

      if (type === "course") {
        setIsAuthenticated(mobileUser?.allowed_profiles !== null);
        navigate("/my-courses");
      } else if (mobileUser?.allowed_profiles !== null) {
        setIsAuthenticated(true);
        setPaymentVerified(true);
      } else {
        setIsAuthenticated(false);
        navigate("/");
      }
    } else {
      navigate("/");
    }
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : prev.length < 3
        ? [...prev, categoryId]
        : prev
    );
  };

  const handleStartExploring = () => {
    navigate("/my-profiles");
  };

  // Show welcome screen after payment verified for plan purchases
  if (paymentVerified) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-4">
        <div className="w-full max-w-lg text-center">
          {/* Celebration */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
            className="mb-8"
          >
            <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={60} weight="fill" className="text-green-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {t("success.welcome") || "Welcome to DeeClass!"}
            </h1>
            <p className="text-gray-400">
              {t("success.ready") || "You're ready to start learning"}
            </p>
          </motion.div>

          {/* Interest Selection */}
          {categories.length > 0 && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mb-8"
            >
              <p className="text-white font-medium mb-4">
                {t("success.interests") || "What are you interested in?"}
              </p>
              <p className="text-gray-500 text-sm mb-6">
                {t("success.select_categories") || "Select up to 3 topics"}
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {categories.map((cat) => {
                  const isSelected = selectedCategories.includes(cat._id);
                  return (
                    <button
                      key={cat._id}
                      onClick={() => toggleCategory(cat._id)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 border
                        ${isSelected
                          ? "bg-primary border-primary text-white"
                          : "bg-grey border-gray-600 text-gray-300 hover:border-gray-400"
                        }`}
                    >
                      {cat.title || cat.translations?.en?.title}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Start Button */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <button
              onClick={handleStartExploring}
              className="w-full max-w-xs mx-auto bg-primary hover:bg-primary/90 text-white py-3.5 px-8 rounded-xl font-semibold
                transition-all duration-300 flex items-center justify-center gap-2"
            >
              {t("success.start_exploring") || "Start Exploring"}
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Loading state while verifying payment
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="text-center">
        <Spinner />
        <p className="text-gray-400 mt-4">
          {t("success.verifying") || "Verifying your payment..."}
        </p>
      </div>
    </div>
  );
};

export default Success;
